import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { useForceDark } from "@/hooks/useForceDark";

export const PRIVACY_VERSION = "2026-02-19";

export default function Privacy() {
  useForceDark();
  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-3 mb-8 hover:opacity-80">
          <img src="/logo.png" alt="Elsenham YFC" className="w-9 h-9 rounded-full object-contain" />
          <div className="font-display font-black text-lg tracking-tight">ELSENHAM YFC</div>
        </Link>
        <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">
          <ShieldCheck className="w-4 h-4" /> Privacy Notice · v{PRIVACY_VERSION}
        </div>
        <h1 className="font-display font-black text-4xl md:text-5xl mt-3 mb-8 leading-tight">How we handle your data.</h1>

        <div className="prose prose-invert prose-sm max-w-none space-y-6 text-white/80" data-testid="privacy-notice-body">
          <section>
            <h2 className="font-display font-bold text-xl text-white">1. Who we are</h2>
            <p>Elsenham Youth Football Club ("we", "us") is the data controller for the personal data collected through this booking platform. Contact the club committee via <a className="text-[#83B68B]" href="mailto:rickylambourne@gmail.com">rickylambourne@gmail.com</a> for any data-protection queries.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">2. What we collect</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Account details</strong> — your name, email address, optional phone number, hashed password.</li>
              <li><strong>Booking activity</strong> — pitches, dates, times and teams you book, plus any notes or opponent name you add.</li>
              <li><strong>Team & player data</strong> (if you upload it) — player names / initials, age group, kit colour, coach details.</li>
              <li><strong>Referee data</strong> (admin only) — name, contact details, qualification status, fees.</li>
              <li><strong>Audit &amp; email history</strong> — a record of changes you make and confirmation emails we send you.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">3. Why we use it</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>To run pitch bookings, assign referees, and issue fixture confirmations to your team.</li>
              <li>To send match-day emails and season admin communications.</li>
              <li>To keep a history of who booked what, so the club can resolve disputes and comply with FA safeguarding.</li>
            </ul>
            <p className="mt-2">Legal basis: performance of a contract with our members, and legitimate interest in running the club safely.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">4. Who we share it with</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Resend</strong> — our email delivery provider (used to send booking &amp; referee assignment emails).</li>
              <li><strong>The FA (Full-Time)</strong> — we only READ fixture information from FA Full-Time; we do not send personal data to them.</li>
              <li>We do not sell, rent or otherwise trade your data.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">5. How long we keep it</h2>
            <p>Account and booking data is kept while you are an active member and for one season after you leave. Audit and email history is retained for up to 24 months and then automatically purged. You can ask for earlier deletion at any time (see rights below).</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">6. Your rights</h2>
            <p>Under UK GDPR you can:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Access a copy of your data</li>
              <li>Correct anything that is wrong</li>
              <li>Ask for your data to be deleted</li>
              <li>Restrict or object to how we use it</li>
              <li>Withdraw consent at any time</li>
              <li>Complain to the ICO (<a className="text-[#83B68B]" href="https://ico.org.uk" target="_blank" rel="noreferrer">ico.org.uk</a>) if you're unhappy with our response</li>
            </ul>
            <p className="mt-2">Email the club committee to exercise any of these rights.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">7. Cookies</h2>
            <p>We use a single authentication cookie and a couple of local-storage keys to remember your login and calendar preferences. We do not run advertising or analytics tracking.</p>
          </section>

          <section className="text-white/50 text-xs pt-6 border-t border-white/10">
            <p>Version {PRIVACY_VERSION}. We'll ask you to re-accept if we materially change how we use your data.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
